import { Dialog, DialogContent, DialogActions } from "@mui/material";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { ModalLoading } from "./modal_loading";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { EmailForm, EmailSchema } from "../presentation/schemas/email";

import Quill from 'quill';
import PrimaryButton from "./primary_button";
import FormContainer from "./form_container";
import DefaultFormInput from "./default_form_input";

import 'quill/dist/quill.snow.css';
import './../../styles/editor.css';

/**
 * SendMailModal
 */

type Props = {
  open: boolean;
  loading: boolean;
  sendTo: string;
  subject: string;
  attachment: string;
  body: string;
  cancelButtonLabel?: string;
  confirmButtonLabel?: string;
  confirmSavingButtonLabel?: string;
  onClose: () => void;
  onSubmit: (form: EmailForm) => void;
};

export const SendMailModal = ({
  open,
  loading,
  sendTo,
  subject,
  attachment,
  body,
  cancelButtonLabel,
  confirmButtonLabel,
  confirmSavingButtonLabel,
  onClose,
  onSubmit,
}: Props) => {
  const hookForm = useForm<EmailForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(EmailSchema),
  })

  useEffect(() => {
    if (open) {
      hookForm.setValue('sendTo', sendTo);
      hookForm.setValue('subject', subject);
      hookForm.setValue('body', body);
      hookForm.setValue('attachment', attachment);
    }
  }, [
    open,
    sendTo,
    subject,
    attachment,
    body,
    hookForm,
  ]);

  const handleSubmit = useCallback((form: EmailForm) => {
    if (!loading) onSubmit(form);
  }, [
    loading,
    onSubmit
  ]);

  const handleClose = useCallback(() => {
    if (!loading) onClose();
  }, [
    loading,
    onClose
  ]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "65rem",
          },
        },
      }}>

      <DialogContent>
        <ModalLoading loading={loading} message="Enviando...">
          <FormContainer title="Enviar Para" padding="small" gap="small" arrangement="fullRow">
            <>
              <DefaultFormInput
                name='sendTo'
                multiline={true}
                rows={4}
                reactHookForm={hookForm} />

              <p className="text-sm text-red-400">
                Informe os emails separando-os por ;
              </p>
            </>
          </FormContainer>

          <FormContainer title="Assunto" padding="small" gap="small" arrangement="fullRow">
            <>
              <DefaultFormInput
                name='subject'
                reactHookForm={hookForm} />
            </>
          </FormContainer>

          <FormContainer title="Anexo" padding="small" gap="small" arrangement="fullRow">
            <>
              <p className="text-black dark:text-white italic underline">
                {attachment}
              </p>
            </>
          </FormContainer>

          <div className="bg-white text-black h-[20rem]">
            <Editor
              defaultValue={body}
              onChange={(v) => { }}
            />
          </div>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-end flex p-4 gap-4">
          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                {cancelButtonLabel ?? 'Cancelar'}
              </h1>
            </div>

            <div className="w-[120px]">
              {loading ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  {confirmSavingButtonLabel ?? 'Enviando...'}
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  {confirmButtonLabel ?? 'Enviar'}
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog >
  );
}

/**
 * Editor
 */

type EditorProps = {
  defaultValue?: string;
  onChange: (value: string) => void;
};

const Editor = forwardRef<Quill, EditorProps>(({ defaultValue, onChange }, ref) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);

  const [editorId] = useState(`quill-editor-${Math.random().toString(36).substring(2, 9)}`);

  useEffect(() => {
    const destroyQuill = () => {
      if (quillRef.current) {
        quillRef.current = null;
      }
      document.querySelectorAll('.ql-toolbar').forEach(toolbar => toolbar.remove());
      if (editorRef.current) {
        editorRef.current.innerHTML = '';
        const editorElement = document.createElement('div');
        editorElement.id = editorId;
        editorRef.current.appendChild(editorElement);
        return editorElement;
      }
      return null;
    };

    if (!quillRef.current) {
      const editorElement = destroyQuill();

      if (!editorElement) return;

      const modules = {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code-block'],

          [{ 'header': 1 }, { 'header': 2 }],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'list': 'check' }],
          [{ 'script': 'sub' }, { 'script': 'super' }],
          [{ 'indent': '-1' }, { 'indent': '+1' }],

          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

          [{ 'color': [] }, { 'background': [] }],
          [{ 'font': [] }],
          [{ 'align': [] }],
        ]
      };

      const quill = new Quill(editorElement, {
        theme: 'snow',
        modules,
        readOnly: false,
      });

      quillRef.current = quill;

      if (defaultValue) {
        quillRef.current.clipboard.dangerouslyPasteHTML(defaultValue);
      }

      quill.on('text-change', () => {
        if (onChange) {
          onChange(quill.root.innerHTML);
        }
      });
    }

    return () => {
      destroyQuill();
    };
  }, [
    editorId,
    onChange,
    defaultValue,
  ]);

  return (
    <div
      ref={editorRef}
      className="w-full h-[85%]" />
  );
})